/**
 * createAppCatalogClient — the PUBLIC app catalog's reader for a marketing site (APP-ENTITLEMENT-001 Phase 11):
 * `GET /v1/catalog/apps`, the ONE public read behind every price a visitor sees — the offered (and coming-soon)
 * apps, their dimensions with PUBLISHED prices, the suggestions, the published processing rate, the annual rule
 * and the trial length.
 *
 * Default basePath is the PUBLIC ingress path `/api/v1/catalog/apps`: this client runs in the browser, so its call
 * traverses the public reverse-proxy, which forwards `^/api` to the API container and strips the prefix before it
 * (the `createMarketingCmsClient` precedent — a bare `/v1/…` falls through to the SPA shell and answers HTML).
 *
 * Transport: an injectable fetch. Production wiring passes `createFetchSeam()`; tests pass a stub. The body is read
 * as a canonical envelope (`readEnvelopeData` throws on anything else) and the payload is judged ONCE by the catalog
 * package's own reader, so an unreadable response reaches the page's error state — never a page that prices nothing.
 *
 * @module @puffin-core/marketing-shell/pricing/createAppCatalogClient
 */

// 🔴 The SUBPATH, never the barrel: `envelope.js` imports nothing, and this client is reachable from nine
// PRERENDERED marketing entries (the `createMarketingCmsClient` precedent).
import { readEnvelopeData } from '@puffin-core/api-client/envelope';
import { readPublicCatalogPayload } from '@puffin-core/app-catalog';

const DEFAULT_BASE_PATH = '/api/v1/catalog/apps';

/**
 * @typedef {Object} AppCatalogClient
 * @property {(opts?: {signal?: AbortSignal}) => Promise<Object>} readCatalog - the public catalog, judged readable.
 * @property {string} url - the URL it reads.
 */

/**
 * @param {Object} config
 * @param {Function} config.fetch - the fetch implementation (DI).
 * @param {string} [config.baseUrl=''] - e.g. 'https://api.parcelpuffin.com'; empty → relative.
 * @param {string} [config.basePath='/api/v1/catalog/apps']
 * @returns {AppCatalogClient}
 * @throws {TypeError} without a fetch function.
 */
export default function createAppCatalogClient(config) {
  if (!config || typeof config !== 'object' || typeof config.fetch !== 'function') {
    throw new TypeError('createAppCatalogClient: `fetch` (function) is required');
  }
  const { fetch, baseUrl = '', basePath = DEFAULT_BASE_PATH } = config;
  const url = `${baseUrl.replace(/\/+$/, '')}${basePath}`;

  /**
   * @param {{signal?: AbortSignal}} [options]
   * @returns {Promise<Object>} the public catalog, judged readable.
   * @throws {Error} carrying the HTTP `status` when the read is refused.
   */
  async function readCatalog({ signal } = {}) {
    const response = await fetch(url, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      signal,
    });
    if (!response.ok) {
      /** @type {Error & {status?: number}} */
      const error = new Error(`appCatalogClient: HTTP ${response.status} from ${url}`);
      error.status = response.status;
      throw error;
    }
    return readPublicCatalogPayload(readEnvelopeData(await response.json()));
  }

  return { readCatalog, url };
}
